import {
  buildQuery,
  countWorkspaceMembers,
  getAllWorkspaces,
  getNotes,
  getSession,
  getUser,
  getUserAvatar,
  getUsers,
  getUsersAvatars,
  getUsersPermissionsForWorkspace,
  getWorkspace,
  getWorkspaceAvatar,
  getWorkspaceMembers,
} from '@nx-frontend/shared/api';
import {
  GetNotesResponse,
  NoteDto,
  NotesPermissionsDto,
  sessionFromDto,
  User,
  UserAvatarDto,
  UserDto,
  userFromDto,
  UserServiceAvatarDto,
  WorkspaceAvatarDto,
  WorkspaceDto,
  WorkspaceMemberDto,
} from '@nx-frontend/shared/types';

export interface GetEntitiesPayload {
  userServiceHost: string;
  workspaceServiceHost: string;
  noteServiceHost: string;
  sessionId: string;
  currentWorkspaceId: string;
}

export interface WorkspaceEnriched extends WorkspaceDto {
  avatar?: WorkspaceAvatarDto | null;
}

export interface WorkspaceMemberEnriched extends WorkspaceMemberDto {
  firstname?: string | null;
  lastname?: string | null;
  email?: string;
  username?: string;
  avatar?: UserServiceAvatarDto | null;
  granularRoles?: {
    workspaceId: string;
    userId: number;
    noteId: string;
    parentId: string;
    role: string;
    isFolder: boolean;
  }[];
}

export interface MentionEntities {
  owner: unknown;
  folders: NoteDto[];
  workspaces: WorkspaceDto[];
  members: WorkspaceMemberEnriched[];
  counters: {
    folder: number;
    workspace: number;
    member: number;
  };
}

export const getEntities = async (payload: GetEntitiesPayload): Promise<MentionEntities> => {
  const { userServiceHost, noteServiceHost, workspaceServiceHost, sessionId, currentWorkspaceId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  const query: unknown = {
    counters: false,
    count: true,
    filter: {
      type: 'folder',
    },
    order: [
      ['dateUpdated', 'DESC'],
      ['globalId', 'DESC'],
    ],
    range: {
      limit: 100,
    },
  };
  const promises1 = [
    getNotes({
      host: noteServiceHost,
      userId,
      workspaceId: currentWorkspaceId,
      query: buildQuery(query),
    }),
    getAllWorkspaces({
      host: workspaceServiceHost,
      userId,
      includeOrgType: false,
      includeAvatar: true,
    }),
    getWorkspaceMembers({
      host: workspaceServiceHost,
      userId,
      workspaceId: currentWorkspaceId,
    }),
  ];
  const results1 = await Promise.all(promises1.map((promise) => promise.catch(() => null)));
  let folders: NoteDto[] = [];
  let foldersCount = 0;
  const workspaces: WorkspaceEnriched[] = [];
  let members: WorkspaceMemberDto[] = [];
  if (results1[0]) {
    const value = results1[0] as GetNotesResponse;
    folders = value.notes;
    foldersCount = value.count;
  }
  if (results1[1]) {
    const _workspaces = results1[1] as WorkspaceDto[];
    // prepare avatars
    for (const workspace of _workspaces) {
      let avatar: WorkspaceAvatarDto | null = null;
      if (workspace.avatarStoredFileUUID) {
        avatar = {
          storedFileUUID: workspace.avatarStoredFileUUID,
          userId: 0,
          workspaceId: workspace.globalId,
          createdAt: 0,
          updatedAt: 0,
        };
      }
      workspaces.push(
        Object.assign({}, workspace, {
          avatar,
        })
      );
    }
  }
  if (results1[2]) {
    members = results1[2] as WorkspaceMemberDto[];
  }

  const usersId = members.map((v) => v.userId);
  let notePermissions: NotesPermissionsDto;
  let users: UserDto[] = [];
  let usersAvatars: UserAvatarDto[] = [];
  const promises2 = [
    getUsersPermissionsForWorkspace({
      host: noteServiceHost,
      userId,
      workspaceId: currentWorkspaceId,
    }),
    getUsers({
      host: userServiceHost,
      userId,
      userIds: usersId,
    }),
    getUsersAvatars({
      host: userServiceHost,
      userId,
      userIds: usersId,
    }),
  ];
  const results2 = await Promise.all(promises2.map((promise) => promise.catch(() => null)));
  if (results2[0]) {
    notePermissions = results2[0] as NotesPermissionsDto;
  }
  if (results2[1]) {
    users = results2[1] as UserDto[];
  }
  if (results2[2]) {
    usersAvatars = results2[2] as UserAvatarDto[];
  }
  const enrichedMembers: WorkspaceMemberEnriched[] = members.map((member) => {
    const userFound = users.find((user) => user.id === member.userId);
    if (!userFound) {
      return { ...member, avatar: usersAvatars.find((v) => v.userId === member.userId) ?? null };
    }
    return Object.assign({}, member, {
      firstname: userFound.firstname,
      lastname: userFound.lastname,
      email: userFound.email,
      username: userFound.username,
      avatar: usersAvatars.find((v) => v.userId === member.userId) ?? null,
      granularRoles: notePermissions.notes.filter((granularRole) => {
        return granularRole.userId === userFound.id && granularRole.workspaceId === member.workspaceId;
      }),
    });
  });

  const workspace = workspaces.find((v) => v.globalId === currentWorkspaceId);
  const owner = enrichedMembers.find((v) => v.userId === workspace?.userId) ?? null;

  const data: MentionEntities = {
    owner,
    folders,
    workspaces,
    members: enrichedMembers,
    counters: {
      folder: foldersCount,
      workspace: workspaces.length,
      member: enrichedMembers.length,
    },
  };
  return data;
};

export interface GetWorkspaceMentionsPayload {
  userServiceHost: string;
  workspaceServiceHost: string;
  sessionId: string;
  workspaceId: string;
}

export interface WorkspaceMentions extends WorkspaceDto {
  membersCount: number;
  owner: User;
}
export const retrieveWorkspaceMentions = async (payload: GetWorkspaceMentionsPayload): Promise<WorkspaceMentions> => {
  const { userServiceHost, workspaceServiceHost, sessionId, workspaceId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));

  const workspacePromise = getWorkspace({
    host: workspaceServiceHost,
    userId,
    workspaceId,
  });
  const workspaceCountMembersPromise = countWorkspaceMembers({
    host: workspaceServiceHost,
    userId,
    workspaceId,
  });

  const [workspace, workspaceCountMembers] = await Promise.all(
    [workspacePromise, workspaceCountMembersPromise].map((promise) => promise.catch(() => null))
  );
  const membersCount =
    workspaceCountMembers && 'object' === typeof workspaceCountMembers && 'count' in workspaceCountMembers
      ? workspaceCountMembers.count
      : 0;
  const owner = userFromDto(
    await getUser({
      host: userServiceHost,
      userId,
    })
  );

  return { ...(workspace as WorkspaceDto), ...{ membersCount, owner } };
};

export interface GetUserMentionsPayload {
  userServiceHost: string;
  workspaceServiceHost: string;
  sessionId: string;
  workspaceId: string;
  requestedUserId: number;
}
export interface UserMentions extends UserDto {
  avatar: UserAvatarDto | null;
}
export const retrieveUserMentions = async (payload: GetUserMentionsPayload): Promise<UserMentions | null> => {
  const { userServiceHost, workspaceServiceHost, sessionId, requestedUserId, workspaceId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));

  const workspaceMembers = await getWorkspaceMembers({
    host: workspaceServiceHost,
    userId,
    workspaceId,
  });

  if (workspaceMembers.find((v) => v.userId === requestedUserId)) {
    const userProfile = userFromDto(
      await getUser({
        host: userServiceHost,
        userId,
        requestedUserId,
      })
    );
    const avatar = await getUserAvatar({
      host: userServiceHost,
      userId: requestedUserId,
    });
    return {
      avatar,
      country: userProfile.country ?? '',
      defaultEncryptionKeyId: userProfile.defaultEncryptionKeyId ?? '',
      displayName: userProfile.displayName ?? '',
      email: userProfile.email ?? '',
      emailConfirmed: userProfile.emailConfirmed ?? false,
      emailDeliverySuspended: userProfile.emailDeliverySuspended ?? false,
      firstname: userProfile.firstname ?? '',
      id: userProfile.id,
      lastname: userProfile.lastname ?? '',
      onlyService: userProfile.onlyService ?? null,
      phone: userProfile.phone ?? '',
      registerDate: userProfile.registerDate,
      service: userProfile.service ?? '',
      subscribeNewsletter: userProfile.subscribeNewsletter ?? false,
      username: userProfile.username ?? '',
    };
  } else {
    return null;
  }
};

export interface GetWorkspacePayload {
  userServiceHost: string;
  workspaceServiceHost: string;
  sessionId: string;
}
export const getWorkspaceEntities = async (payload: GetWorkspacePayload): Promise<WorkspaceEnriched[]> => {
  const { userServiceHost, workspaceServiceHost, sessionId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  const results = await getAllWorkspaces({
    host: workspaceServiceHost,
    userId,
  });
  const workspaces: WorkspaceEnriched[] = [];
  if (results) {
    const _workspaces = results as WorkspaceDto[];
    for (const workspace of _workspaces) {
      let avatar: WorkspaceAvatarDto | null = null;
      try {
        avatar = await getWorkspaceAvatar({
          host: workspaceServiceHost,
          userId,
          workspaceId: workspace.globalId,
        });
      } catch (err) {
        avatar = null;
      }
      workspaces.push(
        Object.assign({}, workspace, {
          avatar,
        })
      );
    }
  }

  return workspaces;
};

export const getMembersEntities = async (payload: GetEntitiesPayload): Promise<WorkspaceMemberEnriched[]> => {
  const { userServiceHost, noteServiceHost, workspaceServiceHost, sessionId, currentWorkspaceId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  const members = await getWorkspaceMembers({
    host: workspaceServiceHost,
    userId,
    workspaceId: currentWorkspaceId,
  });

  const usersId = members.map((v) => v.userId);
  let notePermissions: NotesPermissionsDto;
  let users: UserDto[] = [];
  let usersAvatars: UserAvatarDto[] = [];
  const promises2 = [
    getUsersPermissionsForWorkspace({
      host: noteServiceHost,
      userId,
      workspaceId: currentWorkspaceId,
    }),
    getUsers({
      host: userServiceHost,
      userId,
      userIds: usersId,
    }),
    getUsersAvatars({
      host: userServiceHost,
      userId,
      userIds: usersId,
    }),
  ];
  const results2 = await Promise.all(promises2.map((promise) => promise.catch(() => null)));
  if (results2[0]) {
    notePermissions = results2[0] as NotesPermissionsDto;
  }
  if (results2[1]) {
    users = results2[1] as UserDto[];
  }
  if (results2[2]) {
    usersAvatars = results2[2] as UserAvatarDto[];
  }
  const enrichedMembers: WorkspaceMemberEnriched[] = members.map((member) => {
    const userFound = users.find((user) => user.id === member.userId);
    if (!userFound) {
      return { ...member, avatar: usersAvatars.find((v) => v.userId === member.userId) ?? null };
    }
    return Object.assign({}, member, {
      firstname: userFound.firstname,
      lastname: userFound.lastname,
      email: userFound.email,
      username: userFound.username,
      avatar: usersAvatars.find((v) => v.userId === member.userId) ?? null,
      granularRoles: notePermissions.notes.filter((granularRole) => {
        return granularRole.userId === userFound.id && granularRole.workspaceId === member.workspaceId;
      }),
    });
  });

  return enrichedMembers;
};
